import { createTRPCClient, httpBatchLink } from '@trpc/client'
import { createTRPCReact, type CreateTRPCReact } from '@trpc/react-query'
import type { AnyRouter } from '@trpc/server'

/**
 * Factory function to create tRPC clients with custom auth handling
 * This avoids hardcoding auth store imports in the shared package
 */
export function createTrpcClients<TRouter extends AnyRouter>(options?: {
  onAuthError?: () => void
}): {
  trpc: CreateTRPCReact<TRouter, unknown>
  trpcClient: ReturnType<typeof createTRPCClient<TRouter>>
  enhancedFetch: (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
  ) => Promise<Response>
} {
  const trpc = createTRPCReact<TRouter>()
  let refreshPromise: Promise<boolean> | null = null

  async function enhancedFetch(
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
  ): Promise<Awaited<ReturnType<typeof fetch>>> {
    const response = await fetch(input, init)

    if (response.status === 401) {
      // Check if this is a token expiration that should trigger refresh
      const shouldRefresh = await checkIfTokenExpired(response)

      if (shouldRefresh) {
        const refreshSuccessful = await handleTokenRefresh()
        if (refreshSuccessful) {
          return fetch(input, init)
        }
      }

      options?.onAuthError?.()
    }

    if (!response.ok) {
      console.error('tRPC fetch error:', response.status, response.statusText)
    }

    return response
  }

  async function checkIfTokenExpired(response: Response): Promise<boolean> {
    try {
      const text = await response.clone().text()
      return text.includes('TOKEN_VALIDATION_FAILED')
    } catch {
      return false
    }
  }

  async function handleTokenRefresh(): Promise<boolean> {
    if (refreshPromise) {
      return refreshPromise
    }

    refreshPromise = performTokenRefresh()

    try {
      const result = await refreshPromise
      return result
    } finally {
      refreshPromise = null
    }
  }

  async function performTokenRefresh(): Promise<boolean> {
    try {
      const refreshResponse = await fetch('/api/auth.refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })

      return refreshResponse.ok
    } catch {
      return false
    }
  }

  const trpcClient = createTRPCClient<TRouter>({
    links: [
      httpBatchLink<TRouter>({
        url: '/api',
        headers: () => ({
          'Content-Type': 'application/json',
        }),
        fetch: enhancedFetch,
      } as any), // eslint-disable-line @typescript-eslint/no-explicit-any
    ],
  })

  return { trpc, trpcClient, enhancedFetch }
}
